import type { KkmExecutionResult, KkmServerAdapter, KkmShiftCounters } from '@/shared/api';
import type {
  CloseShiftInput,
  ForceCloseShiftInput,
  ShiftFieldsFragment,
  StartCashShiftClosingMutation,
} from '@shared/api/graphql/gql/graphql';

import { kktSessionClosed, readKktState } from './fiscal-operation-session';
import { executeFiscalShiftAction } from './fiscal-shift-actions';

type ShiftClosingAdapter = Pick<KkmServerAdapter, 'closeShift' | 'getCounters' | 'handshake' | 'openShift' | 'xReport'>;
type ShiftClosingContext = StartCashShiftClosingMutation['startShiftClosing'];
export type ShiftPendingOperation = ShiftClosingContext['pending_operations'][number];

export interface ShiftClosingServices {
  cancel: (shiftId: string) => Promise<ShiftFieldsFragment>;
  close: (input: CloseShiftInput) => Promise<ShiftFieldsFragment>;
  /** Цикл смены: незавершённые операции доводятся до Z-отчёта и смене не мешают. */
  finishPendingOperations: (operations: readonly ShiftPendingOperation[]) => Promise<void>;
  forceClose: (input: ForceCloseShiftInput) => Promise<ShiftFieldsFragment>;
  /** Тот же контекст, но без перевода смены в `closing`: на нём открывается диалог. */
  readContext: (shiftId: string) => Promise<ShiftClosingContext>;
  start: (shiftId: string) => Promise<ShiftClosingContext>;
}

export interface PrepareCashShiftClosingRequest {
  expectedKktDeviceId?: string | null;
  fiscalizationEnabled: boolean;
  shiftId: string;
}

export type CashShiftClosingPreparation =
  | { context: ShiftClosingContext; kind: 'kkt_unavailable'; message: string }
  | {
      context: ShiftClosingContext;
      counters: KkmShiftCounters;
      deviceId: string | null;
      kind: 'ready';
      revenueKktMinor: number | null;
    };

export interface CompleteCashShiftClosingRequest {
  cashierName: string;
  cashierVatin?: string | null;
  closingCash: number;
  comment?: string | null;
  expectedKktDeviceId?: string | null;
  fiscalizationEnabled: boolean;
  revenueKkt?: number | null;
  shiftId: string;
}

export class ShiftClosingController {
  public constructor(
    private readonly adapter: ShiftClosingAdapter,
    private readonly services: ShiftClosingServices,
  ) {}

  /**
   * Read-only: смена остаётся `open`, пока кассир считает деньги. В `closing` её переводит
   * только сабмит, поэтому «открыл диалог и передумал» (или F5) не оставляет статус висеть.
   */
  public async prepare(request: PrepareCashShiftClosingRequest): Promise<CashShiftClosingPreparation> {
    const context = await this.services.readContext(request.shiftId);

    if (!request.fiscalizationEnabled) {
      return {
        context,
        counters: emptyCounters(),
        deviceId: null,
        kind: 'ready',
        revenueKktMinor: null,
      };
    }

    try {
      const handshake = await this.adapter.handshake({
        kktDeviceId: request.expectedKktDeviceId ?? undefined,
      });

      if (handshake.kind !== 'ready') {
        return { context, kind: 'kkt_unavailable', message: handshake.instruction };
      }

      const counters = await this.adapter.getCounters(handshake.kktDevice.idDevice);

      return {
        context,
        counters,
        deviceId: handshake.kktDevice.idDevice,
        kind: 'ready',
        revenueKktMinor: counters.total === null ? null : Math.round(counters.total * 100),
      };
    } catch (error) {
      console.error('[cash-shift.get-counters-failed]', {
        shiftId: request.shiftId,
      });
      return { context, kind: 'kkt_unavailable', message: safeMessage(error) };
    }
  }

  public async complete(request: CompleteCashShiftClosingRequest): Promise<ShiftFieldsFragment> {
    assertCashAmount(request.closingCash);

    // Смена входит в `closing` только здесь: backend принимает `closeShift` лишь у
    // закрывающейся смены. Контекст перечитывается на каждой попытке — `startShiftClosing`
    // идемпотентна, поэтому после reload восстанавливается тот же путь, а не начинается заново.
    const context = await this.services.start(request.shiftId);

    if (!request.fiscalizationEnabled) {
      return this.services.close(closeShiftInput(request));
    }

    await this.services.finishPendingOperations(context.pending_operations);

    // Смену могли закрыть на ККТ мимо Mors или прошлой попыткой: второй Z-отчёт не печатается.
    // Отказ кассы закрыть смену снимается повторным `complete()`: он снова читает состояние.
    const handshake = await readKktState(this.adapter, request.expectedKktDeviceId ?? null);

    if (kktSessionClosed(handshake)) {
      return this.services.close({ ...closeShiftInput(request), kktAlreadyClosed: true });
    }

    await executeFiscalShiftAction(this.adapter, 'close', request.cashierName, request.cashierVatin, { handshake });

    return this.services.close(closeShiftInput(request));
  }

  public async printXReport(deviceId: string): Promise<void> {
    requireCommandSuccess(
      await this.adapter.xReport(deviceId),
      'Не получилось напечатать X-отчёт. Проверьте бумагу и повторите.',
    );
  }

  /** Смена на ККТ уже закрыта: вернуть её в работу нельзя, остаётся закрыть без Z-отчёта. */
  public async cancel(request: PrepareCashShiftClosingRequest): Promise<ShiftFieldsFragment> {
    if (
      request.fiscalizationEnabled &&
      kktSessionClosed(await readKktState(this.adapter, request.expectedKktDeviceId))
    ) {
      throw new Error('Смена на кассе уже закрыта. Закройте смену в Морсе, Z-отчёт печатать не нужно.');
    }

    return this.services.cancel(request.shiftId);
  }

  public forceClose(input: ForceCloseShiftInput): Promise<ShiftFieldsFragment> {
    assertCashAmount(input.closingCash);

    if (input.comment.trim() === '') {
      throw new Error('Для аварийного закрытия укажите причину.');
    }

    return this.services.forceClose({ ...input, comment: input.comment.trim() });
  }
}

function closeShiftInput(request: CompleteCashShiftClosingRequest): CloseShiftInput {
  return {
    closingCash: request.closingCash,
    comment: normalizeOptionalComment(request.comment),
    revenueKkt: normalizeOptionalRevenue(request.revenueKkt),
    shiftId: request.shiftId,
  };
}

function requireCommandSuccess(result: KkmExecutionResult, message: string): void {
  if (result.kind !== 'ok') {
    throw new Error(message);
  }
}

function emptyCounters(): KkmShiftCounters {
  return { cash: null, electronicPayment: null, total: null };
}

function assertCashAmount(value: number): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error('Укажите наличные в кассе на момент закрытия.');
  }
}

function normalizeOptionalRevenue(value: number | null | undefined): number | null {
  return value === undefined || value === null || !Number.isFinite(value) ? null : value;
}

function normalizeOptionalComment(value: string | null | undefined): string | null {
  const normalized = value?.trim();

  return normalized ? normalized : null;
}

function safeMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Касса не отвечает. Смену можно закрыть аварийно.';
}
